import React, { useState, useEffect } from "react";
import { SearchModelEnum } from "types/qbank/searchModel";
import { MetaDataPropertyTypes } from "types/metaDataPropertyTypes";
import { PropertyCriteria } from "types/qbank/propertyCriteria";
import {
  Button,
  Icon,
  Level,
  LevelLeft,
  LevelRight,
  Loader,
  Title,
} from "components/ui";
import AdvancedSearchInput from "./AdvancedSearchInput";
import { useFilter } from "hooks/filter";
import type { AutoSuggestValue } from "types/autoSuggestValue";
import type { SearchState } from "../../../hooks/search";
import { Dialog, Container, Box, Button as MuiButton, Slide } from "@mui/material";
import { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AdvancedSearchPanel(props: {
  active: boolean;
  closePanel: () => void;
  propertyTypes: MetaDataPropertyTypes;
}) {

  const transformToStateFormat = (dataArray: AutoSuggestValue[]) => {
    const transformedData: any = {};
    const propertyCriteria: PropertyCriteria[] = [];
    dataArray.forEach((item: AutoSuggestValue) => {
      if (item.propertyCriteria !== undefined) {
        const { systemName, value } = item.propertyCriteria;
        //HierarchicalTree use ">>" to join the values
        let formattedValues = value;
        if (Array.isArray(value)) {
          formattedValues = value.map((subItem: any) =>
          Array.isArray(subItem) ? subItem.join('>>') : subItem
        );
        }
        transformedData[systemName] = formattedValues;
        propertyCriteria.push(item.propertyCriteria);
      }
    });
    //set the advanced search filters
    setAdvancedSearchFilters(propertyCriteria);
    return transformedData;
  };

  const hasValidActiveFilters = (searchState: SearchState) => {
    return (
      searchState &&
      Array.isArray(searchState.activeFilters) &&
      searchState.activeFilters.length > 0
    );
  };

  useEffect(() => {
    if (hasValidActiveFilters(searchState)) {
      setInputValues(transformToStateFormat(searchState.activeFilters));

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { searchState, searchDispatch } = useFilter();
  let [advancedSearchFilters, setAdvancedSearchFilters] = useState<
    PropertyCriteria[]
  >([]);
  let [inputValues, setInputValues] = useState<{ [key: string]: any }>({});

  function setCriteria(propertyCriteria: PropertyCriteria) {
    setAdvancedSearchFilters((prevFilters) => {
      let clone = prevFilters.filter(
        (existingCriteria) =>
          existingCriteria.systemName !== propertyCriteria.systemName
      );

      if (propertyCriteria.value !== undefined) {
        clone.push(propertyCriteria);
      }

      return clone;
    });
  }


  function search() {
    setAdvancedSearchFilters([]);
    setInputValues({});
    searchDispatch({
      type: "ADD_FILTER",
      payload: {
        criteria: SearchModelEnum.properties,
        value: "",
        overwrite: true,
        propertyCriteria: advancedSearchFilters,
        label: true,
      },
    });

    props.closePanel();
  }

  return (
    <Dialog
      fullScreen
      open={props.active}
      onClose={props.closePanel}
      TransitionComponent={Transition}
    >

      <Level className="level-header">
        <LevelLeft
          className="advanced-search-panel-back is-clickable"
          onClick={props.closePanel}
        >
          <Icon name="arrow-left" />
          <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
            <Title className="title-white text-subheading" size={6}>Back</Title>
          </span>
        </LevelLeft>

        <LevelRight>
        </LevelRight>
      </Level>
      <Box sx={{
        height: 'calc(100vh - 140px)',
        overflow: 'auto',
        pb: 2,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        }
      }}>
        <Container sx={{ py: 3 }}>
      {props.propertyTypes ? (
        searchState.currentCategory.propertyRequests.map((systemName) => (
          <div key={systemName}>
            <AdvancedSearchInput
              systemName={systemName}
              propertyTypes={props.propertyTypes}
              setCriteria={setCriteria}
              value={inputValues[systemName]}
              setInputValues={setInputValues}
            />
          </div>
        ))
      ) : (
        <Loader />
      )}
        </Container>
      </Box>
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderTop: '1.5px solid #009CDF',
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <Button
          onClick={() => setInputValues({})}
        >
          Clear all filters
        </Button>
        <MuiButton
          variant="contained"
          onClick={search}
          sx={{
            backgroundColor: '#023e58',
            color: '#ffffff',
            textTransform: 'none',
            px: 3,
            py: 1,
            '&:hover': {
              backgroundColor: '#009cdf',
            }
          }}
        >
          Search
        </MuiButton>
      </Box>
    </Dialog >
  );
}
